import { Component, Input, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { Subscription } from 'rxjs';
import { Message, User, SendMessageEvent } from '@progress/kendo-angular-conversational-ui';
import { ActivatedRoute } from '@angular/router';
import { NachrichtService } from '@/api/service-index';
import { AceNachrichtAceNachrichtEnumsAceNachrichtStatuscode, GlobalEnumsAceNachrichtentyp, GlobalEnumsAceXfoundportalobjekt, NachrichtDisplayModel, PortalkonfigurationDisplayModel } from '@/api/model-index';
import { ExtendedAuthenticationService } from '../authentication/services/extended-authentication.service';
import { StateService } from '@/core/services/state.service';
import { formatDate } from '@angular/common';
import { I18nService } from '@/core/services/i18nService';
import { StammdatenService } from '@/core/services/stammdaten.service';

@Component({
  selector: 'app-chat',
  templateUrl: './chat.component.html',
  styleUrls: ['./chat.component.scss']
})
export class ChatComponent implements OnInit, OnDestroy {
  @Input()
  public anfrageId: string;
  @Input()
  public scope: GlobalEnumsAceXfoundportalobjekt;

  public feed: Message[] = [];
  public user: User = {
    id: 0
  }
  public countUserId: number = 0;
  public sourceMessages: NachrichtDisplayModel[] = [];
  public subscription: Subscription;
  public answerContextId: string | null;
  public portalkonfiguration: PortalkonfigurationDisplayModel;
  
  @ViewChild('kendoChatRef') kendoChatRef: any;
  public kendoChatListRef: HTMLElement;

  public get IsVisible() {
    return this.stateService.showAnfrageInfo && this.portalkonfiguration.nachrichtenInSidebarAnzeigen;
  }

  constructor(protected route: ActivatedRoute, protected nachrichtService: NachrichtService, protected authenticationService: ExtendedAuthenticationService, protected stateService: StateService, protected langService: I18nService, protected stammdatenService: StammdatenService) {
    this.portalkonfiguration = this.stammdatenService.Portalkonfiguration;
  }

  ngOnInit(): void {
    if (!this.IsVisible)
      return

    this.nachrichtService.apiNachrichtAnfrageIdGet({ anfrageId: this.anfrageId, 'x-anfragescope': this.scope }).subscribe(nachrichten => {
      this.sourceMessages = nachrichten;
      this.setupChat();      
    });

    this.subscription = this.langService.languageChanged$.subscribe(() => {
      this.answerContextId = null;
      this.setupChat();
    })
  }

  ngOnDestroy(): void {
    if (this.subscription)
      this.subscription.unsubscribe();
  }

  public setupChat() {
    let receivedMessages: Message[] = [];

    this.sourceMessages.forEach(nachricht => {
      if (nachricht.nachrichtentyp == GlobalEnumsAceNachrichtentyp.Info) {
        receivedMessages.push(this.convertInfoMessage(nachricht));
      }

      if (nachricht.nachrichtentyp == GlobalEnumsAceNachrichtentyp.Frageeingehend) {
        receivedMessages.push(this.convertMessage(nachricht));  
        if(nachricht.statusgrund == AceNachrichtAceNachrichtEnumsAceNachrichtStatuscode.Antwortgesendet && nachricht.antwort) {
          receivedMessages.push(this.convertCrmMessage(nachricht));
        }
      }

      if (nachricht.nachrichtentyp == GlobalEnumsAceNachrichtentyp.Frageausgehend) {
        let converted = this.convertCrmMessage(nachricht);
        converted.text = nachricht.text ?? "";
        receivedMessages.push(converted);
        if (nachricht.statusgrund == AceNachrichtAceNachrichtEnumsAceNachrichtStatuscode.Antwortgesendet && nachricht.antwort) {
          let convertedAnswer = this.convertMessage(nachricht);
          convertedAnswer.text = nachricht.antwort;
          receivedMessages.push(convertedAnswer);
        }
      }
    });
    this.feed = receivedMessages;
    this.kendoChatListRef = this.kendoChatRef.messageList.nativeElement.firstChild as HTMLElement;
  }

  public sendMessage(e: SendMessageEvent): void {
    if (this.answerContextId == null) {
      let newNachricht = new NachrichtDisplayModel();
      newNachricht.anfrageid = this.anfrageId;
      newNachricht.text = e.message.text;
      this.nachrichtService.apiNachrichtPost({ body: newNachricht, 'x-anfragescope': this.scope }).subscribe((savedNachricht: NachrichtDisplayModel) => {
        this.sourceMessages.push(savedNachricht);
        this.setupChat();
      })
      this.savingMessageAnimation();
    }
    else {
      let answeredMessageIndex = this.sourceMessages.findIndex(message => message.id == this.answerContextId)!;
      let answeredMessage = this.sourceMessages[answeredMessageIndex];
      answeredMessage.anfrageid = this.anfrageId;
      answeredMessage.antwort = e.message.text;
      this.nachrichtService.apiNachrichtAnswerPatch({ body: answeredMessage, 'x-anfragescope': this.scope }).subscribe((updatedNachricht: NachrichtDisplayModel) => {
        this.answerContextId = null;
        this.sourceMessages.splice(answeredMessageIndex, 1);
        this.sourceMessages.push(updatedNachricht);
        this.setupChat();
      })
      let selectedAnswer = this.feed[this.feed.length-1];
      selectedAnswer.author.id = 1;
      this.savingMessageAnimation();
    }
  }

  public savingMessageAnimation() {
    this.feed.push({
      author: this.user,
      typing: true,
      timestamp: new Date()
    });
    this.feed = this.feed.map(x => Object.assign({}, x));
  }

  public formatTimeStamp(timestamp: Date) {
    return timestamp.getHours() + ":" + timestamp.getMinutes();
  }

  public convertMessage(nachricht: NachrichtDisplayModel): Message {
    let timestamp = nachricht.nachrichtentyp == GlobalEnumsAceNachrichtentyp.Frageeingehend ? new Date(Date.parse(nachricht.erstelltAm ?? "")) : new Date(Date.parse(nachricht.bearbeitetAm ?? ""));
    const message: Message = {
      author: {
        id: 0,
        name: nachricht.absender ?? ""
      },
      timestamp: timestamp,
      text: nachricht.text??"",
      suggestedActions: [],
      status: nachricht.id ?? ""
    }
    return message;
  }

  public convertCrmMessage(nachricht: NachrichtDisplayModel): Message {
    let timestamp = nachricht.nachrichtentyp == GlobalEnumsAceNachrichtentyp.Frageeingehend ? new Date(Date.parse(nachricht.bearbeitetAm ?? "")) : new Date(Date.parse(nachricht.erstelltAm ?? ""));
    const message: Message = {
      author: {
        id: ++this.countUserId,
        name: this.portalkonfiguration.absenderNachrichten ?? ""
      },
      timestamp: timestamp,
      text: nachricht.antwort ?? "",
      suggestedActions: [],
      status: nachricht.id ?? ""
    }
    return message;
  }

  public convertInfoMessage(nachricht: NachrichtDisplayModel): Message {
    let timestamp = new Date(Date.parse(nachricht.erstelltAm ?? ""));
    const message: Message = {
      author: {
        id: -1,
        name: this.langService.instant("chat.author.information")
      },
      timestamp: timestamp,
      text: nachricht.text ?? "",
      suggestedActions: [],
      status: nachricht.id ?? ""
    }
    return message;
  }

  public getFormattedTimestamp(timestamp: Date): string {
    return formatDate(timestamp, this.langService.GetDateFormatForChatMessages(), this.langService.language);
  }

  public answerMessageClicked(message: Message) {
    let index = this.feed.indexOf(message);
    let cloned = this.feed.map(x => Object.assign({}, x));
    cloned.splice(index, 1);
    message.author.id = -2;
    cloned.push(message);
    this.feed = cloned;

    this.answerContextId = message.status ?? "";

    setTimeout(() => {
      this.kendoChatListRef.scrollIntoView({ behavior: "smooth", block: "end" });
    }, 100);
  }

  public answerDismissClicked() {
    this.answerContextId = null;
    this.setupChat();
  }

  public isMessageAnswerable(message: Message): boolean {
    let sourceMessage = this.sourceMessages.find(sourceMessage => sourceMessage.id == message.status)!;

    return sourceMessage.nachrichtentyp == GlobalEnumsAceNachrichtentyp.Frageausgehend && sourceMessage.statusgrund == AceNachrichtAceNachrichtEnumsAceNachrichtStatuscode.Nachrichtgesendet;
  }
}
